import { useCallback, useState, type ReactNode } from 'react'
import { messageErreur, COULEUR_STATUT, LIBELLE_STATUT, estStatut, type Etape } from '../../api/onboardingRegles'
import { dateComplete } from './dates'
import type { EvenementJournal } from '../../api/onboarding'
import { Icone } from '../Icone'
import { analyserMarkdown, type Segment } from './markdown'
import './onboarding.css'

/** État d'une action asynchrone : l'erreur est TOUJOURS une phrase lisible, jamais un code. */
export function useAction() {
  const [occupe, setOccupe] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)

  const lancer = useCallback(async (fn: () => Promise<string | void>): Promise<boolean> => {
    setOccupe(true)
    setErreur(null)
    setInfo(null)
    try {
      const message = await fn()
      if (typeof message === 'string') setInfo(message)
      return true
    } catch (e) {
      setErreur(messageErreur(e))
      return false
    } finally {
      setOccupe(false)
    }
  }, [])

  return { occupe, erreur, info, lancer, signaler: setErreur }
}

export function BadgeOnboarding({ statut }: { statut: string }) {
  const s = estStatut(statut) ? statut : null
  return <span className={`badge ${s ? COULEUR_STATUT[s] : 'b-gray'}`}>{s ? LIBELLE_STATUT[s] : statut}</span>
}

export function Frise({ etapes }: { etapes: Etape[] }) {
  return (
    <ol className="onb-frise" aria-label="Étapes du parcours">
      {etapes.map((e, i) => (
        <li key={e.cle} className={e.etat === 'fait' ? 'fait' : e.etat === 'courant' ? 'courant' : ''}>
          <span className="pt">{e.etat === 'fait' ? <Icone nom="check" /> : i + 1}</span>
          {e.libelle}
        </li>
      ))}
    </ol>
  )
}

export function Bloc({ titre, droite, lecture, children }: { titre: string; droite?: ReactNode; lecture?: boolean; children: ReactNode }) {
  return (
    <section className={`onb-bloc${lecture ? ' onb-lecture' : ''}`}>
      <h4>
        {titre}
        {droite && <span className="dr">{droite}</span>}
      </h4>
      {children}
    </section>
  )
}

export const Erreur = ({ texte }: { texte: string | null }) => (texte ? <div className="onb-erreur" role="alert">{texte}</div> : null)
export const Info = ({ texte }: { texte: string | null }) => (texte ? <div className="onb-ok" role="status">{texte}</div> : null)

const LIBELLE_TYPE: Record<EvenementJournal['type'], string> = {
  statut: 'Statut',
  piece: 'Pièce',
  contrat: 'Contrat',
  acceptation_manuelle: 'Acceptation manuelle',
  offre: 'Offre',
}

export function Journal({ evenements }: { evenements: EvenementJournal[] }) {
  if (evenements.length === 0) return <p className="onb-note">Aucun événement enregistré.</p>
  return (
    <ul className="onb-journal">
      {evenements.map((e) => (
        <li key={e.id}>
          <span>
            <span className="typ">{LIBELLE_TYPE[e.type]}</span>
            {e.type === 'statut' ? (
              <>
                {e.de && estStatut(e.de) ? `${LIBELLE_STATUT[e.de]} → ` : ''}
                <b>{e.vers && estStatut(e.vers) ? LIBELLE_STATUT[e.vers] : e.vers}</b>
              </>
            ) : (
              e.detail
            )}
          </span>
          {e.motif && <span className="motif">« {e.motif} »</span>}
          <span className="quand">
            {e.auteur ? `${e.auteur} · ` : ''}
            {dateComplete(e.at)}
          </span>
        </li>
      ))}
    </ul>
  )
}

/* Rendu markdown SANS dangerouslySetInnerHTML : l'analyseur produit un arbre, React échappe chaque texte. */
const Inline = ({ segments }: { segments: Segment[] }) => (
  <>{segments.map((s, i) => (s.gras ? <strong key={i}>{s.texte}</strong> : <span key={i}>{s.texte}</span>))}</>
)

export function MarkdownRendu({ source }: { source: string }) {
  const blocs = analyserMarkdown(source)
  if (blocs.length === 0) return <p className="onb-note">Texte vide.</p>
  return (
    <div className="onb-md">
      {blocs.map((b, i) => {
        if (b.type === 'titre') {
          const T = `h${b.niveau}` as 'h1' | 'h2' | 'h3'
          return <T key={i}><Inline segments={b.segments} /></T>
        }
        if (b.type === 'liste') {
          const items = b.elements.map((el, j) => <li key={j}><Inline segments={el} /></li>)
          return b.ordonnee ? <ol key={i}>{items}</ol> : <ul key={i}>{items}</ul>
        }
        return <p key={i}><Inline segments={b.segments} /></p>
      })}
    </div>
  )
}
